import { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { FaChevronLeft, FaChevronRight } from 'react-icons/fa';

function Events({ events }) {
  const [activeIndex, setActiveIndex] = useState(0);
  const [isPaused, setIsPaused] = useState(false);
  const scrollBlocked = useRef(false);

  useEffect(() => {
    if (isPaused || events.length === 0) return;
    const interval = setInterval(() => {
      setActiveIndex((prev) => (prev + 1) % events.length);
    }, 10000);
    return () => clearInterval(interval);
  }, [isPaused, events.length]);

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'ArrowLeft') goToPrev();
      else if (e.key === 'ArrowRight') goToNext();
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [activeIndex, events.length]);

  useEffect(() => {
    const handleWheel = (e) => {
      if (Math.abs(e.deltaX) > 50 && !scrollBlocked.current) {
        e.preventDefault();
        scrollBlocked.current = true;
        if (e.deltaX > 0) goToNext();
        else goToPrev();
        setTimeout(() => { scrollBlocked.current = false; }, 800);
      }
    };
    window.addEventListener('wheel', handleWheel, { passive: false });
    return () => window.removeEventListener('wheel', handleWheel);
  }, [activeIndex, events.length]);

  const getVisibleEvents = () => {
    const result = [];
    for (let i = -2; i <= 2; i++) {
      const idx = (activeIndex + i + events.length) % events.length;
      result.push({ ...events[idx], originalIndex: idx });
    }
    return result;
  };

  const goToPrev = () => setActiveIndex((activeIndex - 1 + events.length) % events.length);
  const goToNext = () => setActiveIndex((activeIndex + 1) % events.length);

  if (events.length === 0) return null;

  return (
    <div 
      className="flex items-center justify-center w-full min-h-[60vh] bg-white py-12"
      onMouseEnter={() => setIsPaused(true)}
      onMouseLeave={() => setIsPaused(false)}
    >
      <div className="w-full max-w-6xl px-0 md:px-4">
        <h1 className="font-custom md:text-7xl text-3xl text-center font-bold text-mainBlue mb-8 px-4 md:px-0">Gallery</h1>
        
        <div className="relative px-0 md:px-0">
          <div className="flex flex-col md:flex-row gap-4 md:items-center">
            <div className="flex flex-row items-center gap-2 md:gap-0">
              <button 
                onClick={goToPrev} 
                className="shrink-0 md:absolute md:left-0 md:-translate-x-14 z-20 text-mainBlue hover:text-blue-600 p-3 md:p-2 bg-white/90 md:bg-gray-100 rounded-full hover:bg-gray-200 transition-all shadow-lg"
                aria-label="Previous event"
              >
                <FaChevronLeft size={16} />
              </button>
              
              <Link
                to={`/${events[activeIndex].id}`}
                className="group relative flex-1 aspect-[16/9] overflow-hidden rounded-xl shadow-xl"
              >
                <img
                  src={events[activeIndex].mainImage}
                  alt={events[activeIndex].title}
                  className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent" />
                {/* Camera badge — always visible, top-right */}
                <div className="absolute top-3 right-3 flex items-center gap-1.5 bg-black/50 backdrop-blur-sm text-white text-xs px-2.5 py-1 rounded-full">
                  <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 13a3 3 0 11-6 0 3 3 0 016 0z" />
                  </svg>
                  Photo Gallery
                </div>
                <div className="absolute bottom-0 left-0 right-0 p-4 md:p-6 text-white">
                  <h2 className="text-xl md:text-3xl font-bold mb-1">{events[activeIndex].title}</h2>
                  <p className="text-gray-300 text-sm line-clamp-1 mb-3">{events[activeIndex].description}</p>
                  <span className="inline-flex items-center gap-1.5 text-xs font-medium bg-white/20 group-hover:bg-white/30 backdrop-blur-sm px-3 py-1.5 rounded-full transition-colors">
                    View Photos
                    <svg className="w-3 h-3 transition-transform group-hover:translate-x-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                  </span>
                </div>
              </Link>
              
              <button 
                onClick={goToNext} 
                className="shrink-0 md:absolute md:right-0 md:translate-x-14 z-20 text-mainBlue hover:text-blue-600 p-3 md:p-2 bg-white/90 md:bg-gray-100 rounded-full hover:bg-gray-200 transition-all shadow-lg"
                aria-label="Next event"
              >
                <FaChevronRight size={16} />
              </button>
            </div>
            
            <div className="flex md:flex-col gap-2 w-full md:w-1/4 px-4 md:px-0">
              {getVisibleEvents().map((event, idx) => {
                const isActive = idx === 2;
                return (
                  <Link
                    key={`${event.id}-${idx}`}
                    to={`/${event.id}`}
                    className={`flex items-center gap-3 p-2 rounded-lg transition-all ${
                      isActive 
                        ? 'bg-mainBlue text-white shadow-md' 
                        : 'bg-gray-100 hover:bg-gray-200 opacity-60 hover:opacity-80'
                    }`}
                  >
                    <img 
                      src={event.mainImage} 
                      alt={event.title}
                      className="w-12 h-12 rounded object-cover flex-shrink-0"
                    />
                    <div className="flex-1 min-w-0">
                      <h3 className={`text-sm font-semibold truncate ${
                        isActive ? 'text-white' : 'text-mainBlue'
                      }`}>
                        {event.title}
                      </h3>
                      <div className={`text-xs ${
                        isActive ? 'text-gray-300' : 'text-gray-500'
                      }`}>
                        {event.date}
                      </div>
                    </div>
                  </Link>
                );
              })}
            </div>
          </div>
          
          <div className="flex justify-center gap-1.5 mt-6">
            {events.map((_, idx) => (
              <button
                key={idx}
                onClick={() => setActiveIndex(idx)}
                className={`h-2 rounded-full transition-all ${
                  idx === activeIndex ? 'bg-mainBlue w-8' : 'bg-gray-300 w-2 hover:bg-gray-400'
                }`}
              />
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

export default Events;
